/*
 * WhyMembersStay — Stage 15 P3 conversion confidence.
 *
 * Calm reassurance block placed near billing / upgrade surfaces.
 * Five plain-language reasons, no hype, no pressure copy.
 *
 * Used by:
 *   • BillingPage.jsx       — under the "Subscribe Now" CTA
 *   • PricingPage.jsx       — between the $9.99 card and the FAQ
 */
import React from 'react';
import {
  ChatCircleDots, FileText, CalendarBlank, Brain, Compass,
} from '@phosphor-icons/react';

const REASONS = [
  {
    icon: ChatCircleDots,
    title: 'Unlimited health questions',
    body: 'Ask Kindra as often as you like. About a result, a symptom, a medication, a diagnosis. Real answers, your context.',
  },
  {
    icon: FileText,
    title: 'Report explanations on demand',
    body: 'Drop in a pathology, an imaging result, a GP letter. Plain English back, in under a minute.',
  },
  {
    icon: CalendarBlank,
    title: 'Appointment preparation',
    body: 'A one-pager you can hand your doctor — symptoms, recent reports, the questions worth asking.',
  },
  {
    icon: Brain,
    title: 'Personal health memory',
    body: 'Kindra remembers between visits — your reports, your medications, what you’ve already asked.',
  },
  {
    icon: Compass,
    title: 'Ongoing guidance between appointments',
    body: 'The space your GP doesn’t have. Calm, considered, available.',
  },
];

export const WhyMembersStay = ({ heading = 'Why members stay', className = '' }) => {
  return (
    <section
      className={`rounded-2xl border border-[#ECE8E1] bg-white p-6 lg:p-8 ${className}`}
      data-testid="why-members-stay"
    >
      <p className="text-[10px] tracking-[0.18em] uppercase text-slate-400 font-medium mb-2">
        Continuing care
      </p>
      <h3
        className="text-[22px] sm:text-[24px] text-[#0E1C2E] leading-tight tracking-tight font-normal mb-5"
        style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
      >
        {heading.includes('stay')
          ? heading
          : <>{heading}</>}
      </h3>
      <ul className="space-y-4">
        {REASONS.map(({ icon: Icon, title, body }) => (
          <li key={title} className="flex gap-4">
            <div className="shrink-0 w-9 h-9 rounded-lg bg-[#235347]/10 flex items-center justify-center mt-0.5">
              <Icon size={18} className="text-[#235347]" weight="duotone" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[14px] font-medium text-[#0E1C2E] leading-snug">{title}</p>
              <p className="text-[13px] text-slate-500 mt-0.5 leading-relaxed">{body}</p>
            </div>
          </li>
        ))}
      </ul>
      <p className="text-[11.5px] text-slate-400 italic mt-5 leading-relaxed">
        Kindra does not diagnose, prescribe, or replace your doctor. It helps you understand what's already there.
      </p>
    </section>
  );
};

export default WhyMembersStay;
